import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { agrogestao } from '@/api/agrogestaoClient';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export function ConfigAdmin({ propriedades = [] }) {
    const [precos, setPrecos] = useState({});
    const [refresh, setRefresh] = useState(0);
    const [dpoEmail, setDpoEmail] = useState('');
    const [policyUrl, setPolicyUrl] = useState('');
    const [termsUrl, setTermsUrl] = useState('');

    const toCurrency = (cents) => {
        const v = Number(cents || 0) / 100;
        return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    };

    const parseCurrency = (s) => {
        const num = String(s || '').replace(/[^0-9]/g, '');
        return Number(num || 0);
    };

    useEffect(() => {
        const map = {};
        (propriedades || []).forEach(p => {
            map[p.id] = {
                mensal: toCurrency(p.valor_licenca_mensal ?? 20000),
                anual: toCurrency(p.valor_licenca_anual ?? 200000)
            };
        });
        setPrecos(prev => {
            // Only update if empty to avoid overwriting user edits? 
            // Or strictly sync? The legacy behavior was sync on mount/prop change.
            if (Object.keys(prev).length === 0) return map;
            return prev;
        });
    }, [propriedades]);

    const { data: configs = [] } = useQuery({
        queryKey: ['configuracoes', refresh],
        queryFn: async () => {
            try {
                const { data, error } = await agrogestao.client.from('configuracoes').select('id, valor');
                if (error) return [];
                return data || [];
            } catch { return []; }
        }
    });

    const getConfig = (key) => configs.find(c => c.id === key)?.valor || '';

    useEffect(() => {
        setDpoEmail(getConfig('dpo_email'));
        setPolicyUrl(getConfig('politica_privacidade_url'));
        setTermsUrl(getConfig('termos_uso_url'));
    }, [configs]);

    const salvarPreco = async (p) => {
        const vals = precos[p.id] || {};
        const mensal = parseCurrency(vals.mensal);
        const anual = parseCurrency(vals.anual);
        try {
            const { data } = await agrogestao.functions.invoke('atualizarPrecoLicencaPropriedade', { propriedade_id: p.id, valor_licenca_mensal: mensal, valor_licenca_anual: anual });
            if (data?.success === false) throw new Error(data?.error || 'erro');
            await agrogestao.access.logAudit({ propriedade_id: p.id, acao: 'atualizar_preco_licenca', table: 'propriedades', status: 'success', details: { mensal, anual } });
            alert('Preços atualizados com sucesso');
        } catch (e) {
            await agrogestao.access.logAudit({ propriedade_id: p.id, acao: 'atualizar_preco_licenca', table: 'propriedades', status: 'error', details: { error: String(e?.message || e) } });
            alert('Falha ao atualizar preços');
        }
    };

    const salvarConfigs = async () => {
        try {
            const payloads = [
                { id: 'dpo_email', valor: dpoEmail },
                { id: 'politica_privacidade_url', valor: policyUrl },
                { id: 'termos_uso_url', valor: termsUrl }
            ];
            for (const p of payloads) {
                await agrogestao.client.from('configuracoes').upsert(p);
            }
            setRefresh(x => x + 1);
            alert('Configurações salvas');
        } catch (e) {
            alert('Falha ao salvar configurações');
        }
    };

    return (
        <div className="space-y-3">
            <div className="text-sm text-stone-600">Ajuste os preços padrão de licença para novas propriedades.</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {propriedades.map((p, i) => (
                    <Card key={p.id} className={[
                        'bg-indigo-50 border-indigo-200',
                        'bg-emerald-50 border-emerald-200',
                        'bg-rose-50 border-rose-200',
                        'bg-sky-50 border-sky-200',
                        'bg-amber-50 border-amber-200',
                        'bg-violet-50 border-violet-200',
                        'bg-teal-50 border-teal-200',
                        'bg-cyan-50 border-cyan-200'
                    ][i % 8]}
                    >
                        <CardHeader><CardTitle>{p.nome || p.id}</CardTitle></CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <div className="text-xs text-stone-600 mb-1">Mensal</div>
                                    <Input value={(precos[p.id]?.mensal) || toCurrency(p.valor_licenca_mensal)} onChange={(e) => {
                                        const v = e.target.value;
                                        setPrecos(prev => ({ ...prev, [p.id]: { ...(prev[p.id] || {}), mensal: v } }));
                                    }} />
                                </div>
                                <div>
                                    <div className="text-xs text-stone-600 mb-1">Anual</div>
                                    <Input value={(precos[p.id]?.anual) || toCurrency(p.valor_licenca_anual)} onChange={(e) => {
                                        const v = e.target.value;
                                        setPrecos(prev => ({ ...prev, [p.id]: { ...(prev[p.id] || {}), anual: v } }));
                                    }} />
                                </div>
                            </div>
                            <div className="mt-3">
                                <Button onClick={() => salvarPreco(p)} className="bg-indigo-600 hover:bg-indigo-700 w-full">Salvar preço</Button>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
            <div className="mt-6 space-y-3 p-4 bg-stone-50 rounded border">
                <div className="text-sm font-medium text-stone-900 border-b pb-2">Configurações LGPD</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                        <Label className="text-xs">E-mail do DPO</Label>
                        <Input value={dpoEmail} onChange={(e) => setDpoEmail(e.target.value)} />
                    </div>
                    <div>
                        <Label className="text-xs">URL da Política de Privacidade</Label>
                        <Input value={policyUrl} onChange={(e) => setPolicyUrl(e.target.value)} />
                    </div>
                    <div>
                        <Label className="text-xs">URL dos Termos de Uso</Label>
                        <Input value={termsUrl} onChange={(e) => setTermsUrl(e.target.value)} />
                    </div>
                </div>
                <Button onClick={salvarConfigs} className="bg-indigo-600 hover:bg-indigo-700">Salvar LGPD</Button>
            </div>
        </div>
    );
}
